import { type FormEvent, useState } from "react";
import { cn } from "../lib/cn.js";

export type LoginResult = { ok: boolean; error?: string };

export function LoginForm({
  onSubmit,
}: {
  onSubmit: (email: string, password: string) => Promise<LoginResult>;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const result = await onSubmit(email, password);
      if (!result.ok) setError(result.error ?? "Email or password is incorrect");
    } catch {
      setError("Could not reach the server");
    } finally {
      setPending(false);
    }
  }

  const field =
    "w-full border border-border bg-surface px-4 py-3 text-text " +
    "outline-none focus:border-accent";

  return (
    <form onSubmit={handle} className="flex w-full max-w-sm flex-col gap-4" noValidate>
      <div className="flex flex-col gap-2">
        <label htmlFor="email" className="text-sm text-text-muted">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={field}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="password" className="text-sm text-text-muted">
          Password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={field}
        />
      </div>

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={pending}
        className={cn(
          "mt-2 bg-accent px-4 py-3 font-medium text-surface",
          "disabled:cursor-not-allowed disabled:opacity-60",
        )}
      >
        {pending ? "Signing in..." : "Sign in"}
      </button>
    </form>
  );
}

export function LoginPage({
  onSubmit,
}: {
  onSubmit: (e: string, p: string) => Promise<LoginResult>;
}) {
  return (
    <main className="flex min-h-full items-center justify-center p-8">
      <div className="flex w-full max-w-sm flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-display-lg">Savi</h1>
          <p className="text-sm text-text-muted">NSE screener and signals</p>
        </div>
        <LoginForm onSubmit={onSubmit} />
      </div>
    </main>
  );
}
